import React, { useEffect, useRef, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import { brandColors } from '@config/brandTokens';
import { resolveContestVideoSrc } from '@utils/contestSubmissionStatus';
import { VideoReactionBar } from '@components/profile/VideoReactionBar';

export interface ProfileVideoItem {
  skillName?: string;
  sport?: string;
  score?: number | null;
  videoUrl?: string;
  thumbnailUrl?: string | null;
  videoKey?: string;
  quarter?: string | null;
  kind?: 'profile' | 'improvement';
  recordedDate?: string | null;
  caption?: string | null;
  selfAttempts?: number | string | null;
  selfSuccesses?: number | string | null;
}

function captureFrame(video: HTMLVideoElement): string | null {
  try {
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return null;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', 0.82);
  } catch {
    return null;
  }
}

const VideoSnapCard: React.FC<{
  video: ProfileVideoItem;
  reaction?: { counts?: Record<string, number>; mine?: Record<string, boolean> };
  compact?: boolean;
}> = ({ video, reaction, compact }) => {
  const src = resolveContestVideoSrc(video.videoUrl);
  const [snap, setSnap] = useState<string>(video.thumbnailUrl || '');
  const [open, setOpen] = useState(false);
  const [livePreview, setLivePreview] = useState(!video.thumbnailUrl);
  const hiddenRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    setSnap(video.thumbnailUrl || '');
    setLivePreview(!video.thumbnailUrl);
  }, [video.thumbnailUrl, video.videoUrl]);

  useEffect(() => {
    if (snap || !src) return;
    const node = hiddenRef.current;
    if (!node) return;
    const grab = () => {
      if (node.readyState < 1) return;
      if (node.currentTime < 0.08) {
        try {
          node.currentTime = 0.12;
        } catch {
          /* seek may fail until enough data */
        }
        return;
      }
      const frame = captureFrame(node);
      if (frame) {
        setSnap(frame);
        setLivePreview(false);
      }
    };
    node.addEventListener('loadeddata', grab);
    node.addEventListener('seeked', grab);
    return () => {
      node.removeEventListener('loadeddata', grab);
      node.removeEventListener('seeked', grab);
    };
  }, [snap, src]);

  if (!src) return null;

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen(true);
          }
        }}
        sx={{
          position: 'relative',
          border: '1px solid #e2e8f0',
          borderRadius: 1.25,
          overflow: 'hidden',
          bgcolor: '#fff',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          '&:hover .play-overlay': { opacity: 1 },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            bgcolor: '#0f172a',
            aspectRatio: '1 / 1',
            width: '100%',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {snap ? (
            <Box
              component="img"
              src={snap}
              alt={video.skillName || 'Profile video'}
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : livePreview ? (
            <Box
              component="video"
              src={src}
              muted
              playsInline
              preload="metadata"
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: '#111739' }} />
          )}
          <Box
            className="play-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(180deg, rgba(17,23,57,0.04) 30%, rgba(17,23,57,0.38) 100%)',
              opacity: 0.88,
            }}
          >
            <PlayCircleFilledWhiteIcon sx={{ fontSize: 26, color: '#fff' }} />
          </Box>
          {video.score != null ? (
            <Box
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                bgcolor: brandColors.orange,
                color: '#fff',
                fontWeight: 800,
                fontSize: 10,
                px: 0.6,
                py: 0.15,
                borderRadius: 0.75,
              }}
            >
              {Number(video.score).toFixed(1)}
            </Box>
          ) : null}
        </Box>
        <Box sx={{ px: 0.85, py: 0.7 }}>
          <Typography
            sx={{
              fontWeight: 800,
              color: brandColors.navy,
              fontSize: 12,
              lineHeight: 1.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {video.skillName || 'Performance'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: brandColors.slate,
              fontWeight: 600,
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {[
              video.sport,
              video.quarter?.replace('-', ' '),
              video.kind === 'improvement' ? 'Improvement' : 'Profile',
              video.selfAttempts != null && video.selfSuccesses != null
                ? `${video.selfSuccesses}/${video.selfAttempts}`
                : '',
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
          <Box onClick={(event) => event.stopPropagation()} sx={{ mt: 0.1 }}>
            <VideoReactionBar videoKey={video.videoKey} initial={reaction} dense />
          </Box>
        </Box>
        <Box
          component="video"
          ref={hiddenRef}
          src={src}
          muted
          playsInline
          preload="auto"
          crossOrigin="anonymous"
          sx={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        />
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: brandColors.navy, pr: 6 }}>
          {video.skillName || 'Profile video'}
          <Typography component="span" sx={{ display: 'block', fontSize: 13, color: brandColors.slate, fontWeight: 600 }}>
            {[video.sport, video.quarter?.replace('-', ' ')].filter(Boolean).join(' · ')}
          </Typography>
        </DialogTitle>
        <IconButton aria-label="Close" onClick={() => setOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ bgcolor: '#0f172a', p: 0 }}>
          <Box component="video" src={src} controls autoPlay playsInline sx={{ width: '100%', display: 'block', maxHeight: 480 }} />
        </DialogContent>
        {video.caption ? (
          <Stack sx={{ px: 2, py: 1.25 }}>
            <Typography variant="body2" sx={{ color: brandColors.slate }}>{video.caption}</Typography>
          </Stack>
        ) : null}
      </Dialog>
    </>
  );
};

export default VideoSnapCard;
